/**
 * Слой предметов на полу: мебель и всё, что стоит свободно, — вид сверху.
 *
 * Каждый предмет переносится в свой центр и поворачивается; как он выглядит,
 * знает компонент его раздела каталога. Порядок — снизу вверх: ковры лежат под мебелью,
 * потом всё по высоте низа — ноутбук на столе и телевизор на тумбе рисуются поверх неё,
 * и клик по ним выделяет их, а не стол. Квадратной подложки под мебелью нет — предмет
 * залит белым только по своему контуру, — поэтому ковёр под диваном вырезается маской по габаритам остальных
 * предметов, иначе его кайма просвечивала бы сквозь диван. У выделенного предмета — ручка поворота над
 * ним: тянешь — предмет крутится вокруг центра, — и ручки размера на серединах сторон:
 * тянешь сторону — предмет растёт или сжимается, а противоположная сторона стоит на месте.
 * Пока тянешь, у ручки подписан новый размер. Ручек размера нет у предметов, чей раздел
 * каталога размеры менять не даёт.
 */
import { useId, type PointerEvent } from "react";
import type { FloorItem } from "../../plan";
import {
	getCatalogItem,
	isRoundItem,
	isRug,
	sectionOfItem,
	type CatalogItem,
} from "@/shared/catalog";
import { CatalogSectionKind, itemDimensions, type ResizeAnchor } from "@/shared/plan";
import type { SizeHint } from "@/shared/hooks";
import { FurnitureItemShape, PlumbingFloorShape, RugShape } from "@/widgets/catalog";
import { SelectionKind, Tool, useEditorStore } from "@/widgets/editor";
import { useLengthUnit } from "@/shared/store";
import { formatMillimeters } from "@/shared/lib/geometry";

/** Ярус предмета на плане: ковёр ниже всего, остальное — по отметке низа. */
const planLevel = (item: FloorItem, info: CatalogItem | null): number =>
	isRug(info) ? -1 : (item.elevation ?? info?.elevation ?? 0);

/** Запас вокруг маски ковра, мм: край покрытия не должен срезаться антиалиасингом. */
const RUG_MASK_PADDING = 4;

/** Насколько ручка поворота вынесена над предметом, пикселей экрана. */
const ROTATE_HANDLE_OFFSET = 22;
/** Сторона квадратной ручки размера, пикселей экрана. */
const RESIZE_HANDLE = 9;
const ACCENT = "#2f6feb";

/** Ручка размера: на какой стороне, какая ось и какая сторона при этом стоит. */
interface ResizeHandle {
	axis: "width" | "depth";
	anchor: ResizeAnchor;
	/** Направление от центра к ручке в осях символа */
	dir: { x: number; y: number };
}

const RESIZE_HANDLES: ResizeHandle[] = [
	{ axis: "width", anchor: "start", dir: { x: 1, y: 0 } },
	{ axis: "width", anchor: "end", dir: { x: -1, y: 0 } },
	{ axis: "depth", anchor: "start", dir: { x: 0, y: 1 } },
	{ axis: "depth", anchor: "end", dir: { x: 0, y: -1 } },
];

/** Курсор-стрелка вдоль направления ручки с учётом поворота предмета. */
const resizeCursor = (handle: ResizeHandle, rotation: number) => {
	const angle = (((rotation + (handle.axis === "width" ? 0 : 90)) % 180) + 180) % 180;
	if (angle < 22.5 || angle >= 157.5) return "ew-resize";
	if (angle < 67.5) return "nwse-resize";
	if (angle < 112.5) return "ns-resize";
	return "nesw-resize";
};

interface Props {
	items: FloorItem[];
	mmPerPx: number;
	/** Инструмент установки активен: клики по предметам не перехватываем */
	placingActive: boolean;
	onItemPointerDown: (e: PointerEvent<Element>, item: FloorItem) => void;
	onRotatePointerDown: (e: PointerEvent<Element>, item: FloorItem) => void;
	onResizePointerDown: (
		e: PointerEvent<Element>,
		item: FloorItem,
		axis: "width" | "depth",
		anchor: ResizeAnchor,
	) => void;
	/** Размер, который сейчас тянут, — подписать у ручки */
	sizeHint: SizeHint | null;
}

export const FloorItemsLayer2D = ({
	items,
	mmPerPx,
	placingActive,
	onItemPointerDown,
	onRotatePointerDown,
	onResizePointerDown,
	sizeHint,
}: Props) => {
	const selection = useEditorStore((s) => s.selection);
	const tool = useEditorStore((s) => s.tool);
	const selectedId = selection?.kind === SelectionKind.Item ? selection.id : null;
	const selected = items.find((item) => item.id === selectedId) ?? null;
	const selectedInfo = selected ? getCatalogItem(selected.ref) : null;
	const selectedSize = selected && selectedInfo ? itemDimensions(selected, selectedInfo) : null;
	const selectedDepth = selectedSize?.depth ?? 0;
	const resizable = selected ? (sectionOfItem(selected.ref)?.canChangeDimensions ?? false) : false;
	// Id из useId со спецсимволами, в url(#…) маски они недопустимы
	const rugMaskId = `rug-mask-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;

	const sorted = items
		.map((item) => ({ item, info: getCatalogItem(item.ref) }))
		.toSorted((a, b) => planLevel(a.item, a.info) - planLevel(b.item, b.info));
	const rugs = sorted.filter(({ info }) => isRug(info));
	const others = sorted.filter(({ info }) => !isRug(info));
	const rugMaskBounds = rugs.reduce(
		(bounds, { item, info }) => {
			if (!info) return bounds;
			const { width, depth } = itemDimensions(item, info);
			const angle = (item.rotation * Math.PI) / 180;
			const halfWidth = (Math.abs(Math.cos(angle)) * width + Math.abs(Math.sin(angle)) * depth) / 2;
			const halfDepth = (Math.abs(Math.sin(angle)) * width + Math.abs(Math.cos(angle)) * depth) / 2;
			return {
				minX: Math.min(bounds.minX, item.x - halfWidth),
				minY: Math.min(bounds.minY, item.y - halfDepth),
				maxX: Math.max(bounds.maxX, item.x + halfWidth),
				maxY: Math.max(bounds.maxY, item.y + halfDepth),
			};
		},
		{ minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
	);
	const rugMaskArea = {
		x: rugMaskBounds.minX - RUG_MASK_PADDING,
		y: rugMaskBounds.minY - RUG_MASK_PADDING,
		width: rugMaskBounds.maxX - rugMaskBounds.minX + RUG_MASK_PADDING * 2,
		height: rugMaskBounds.maxY - rugMaskBounds.minY + RUG_MASK_PADDING * 2,
	};

	const renderItem = ({ item, info }: { item: FloorItem; info: CatalogItem | null }) => {
		// Как выглядит предмет, знает его раздел: сантехника контуром, мебель символом,
		// ковёр — своим цветом без подложки
		const Shape = isRug(info)
			? RugShape
			: item.ref.catalog === CatalogSectionKind.Plumbing ||
				  item.ref.catalog === CatalogSectionKind.Water
				? PlumbingFloorShape
				: FurnitureItemShape;
		return (
			<g
				key={item.id}
				transform={`translate(${item.x} ${item.y}) rotate(${item.rotation})${item.mirrored ? " scale(-1 1)" : ""}`}
			>
				<Shape
					item={item}
					info={info}
					selected={item.id === selectedId}
					mmPerPx={mmPerPx}
					interactive={tool === Tool.Select || !placingActive}
					onPointerDown={(e) => onItemPointerDown(e, item)}
				/>
			</g>
		);
	};

	return (
		<>
			{rugs.length > 0 && (
				<>
					{/* Маска ковров: всё поле видно, габариты предметов на полу — вырезаны */}
					<mask
						id={rugMaskId}
						maskUnits="userSpaceOnUse"
						x={rugMaskArea.x}
						y={rugMaskArea.y}
						width={rugMaskArea.width}
						height={rugMaskArea.height}
					>
						<rect
							x={rugMaskArea.x}
							y={rugMaskArea.y}
							width={rugMaskArea.width}
							height={rugMaskArea.height}
							fill="#ffffff"
						/>
						{others.map(({ item, info }) => {
							if (!info) return null;
							const { width, depth } = itemDimensions(item, info);
							const transform = `translate(${item.x} ${item.y}) rotate(${item.rotation})`;
							return isRoundItem(info) ? (
								<ellipse
									key={item.id}
									cx={0}
									cy={0}
									rx={width / 2}
									ry={depth / 2}
									fill="#000000"
									transform={transform}
								/>
							) : (
								<rect
									key={item.id}
									x={-width / 2}
									y={-depth / 2}
									width={width}
									height={depth}
									fill="#000000"
									transform={transform}
								/>
							);
						})}
					</mask>
					<g mask={`url(#${rugMaskId})`}>{rugs.map(renderItem)}</g>
				</>
			)}
			<g>{others.map(renderItem)}</g>

			{tool === Tool.Select && selected && (
				<g transform={`translate(${selected.x} ${selected.y}) rotate(${selected.rotation})`}>
					<line
						x1={0}
						y1={-selectedDepth / 2}
						x2={0}
						y2={-selectedDepth / 2 - ROTATE_HANDLE_OFFSET * mmPerPx}
						stroke="#2f6feb"
						strokeWidth={1 * mmPerPx}
						style={{ pointerEvents: "none" }}
					/>
					<circle
						cx={0}
						cy={-selectedDepth / 2 - ROTATE_HANDLE_OFFSET * mmPerPx}
						r={6 * mmPerPx}
						fill="#ffffff"
						stroke="#2f6feb"
						strokeWidth={1.5 * mmPerPx}
						style={{ cursor: "grab" }}
						data-role="rotate"
						onPointerDown={(e) => onRotatePointerDown(e, selected)}
					/>
					{resizable &&
						selectedSize &&
						RESIZE_HANDLES.map((handle) => {
							const side = RESIZE_HANDLE * mmPerPx;
							const cx = (handle.dir.x * selectedSize.width) / 2;
							const cy = (handle.dir.y * selectedSize.depth) / 2;
							return (
								<rect
									key={`${handle.axis}-${handle.anchor}`}
									x={cx - side / 2}
									y={cy - side / 2}
									width={side}
									height={side}
									rx={2 * mmPerPx}
									fill="#ffffff"
									stroke={ACCENT}
									strokeWidth={1.5 * mmPerPx}
									style={{ cursor: resizeCursor(handle, selected.rotation) }}
									data-role="resize"
									onPointerDown={(e) =>
										onResizePointerDown(e, selected, handle.axis, handle.anchor)
									}
								/>
							);
						})}
				</g>
			)}

			{selected && selectedSize && sizeHint?.id === selected.id && (
				<SizeLabel item={selected} size={selectedSize} hint={sizeHint} mmPerPx={mmPerPx} />
			)}
		</>
	);
};

/**
 * Подпись размера у стороны, которую тянут: «1600 мм» на плашке, всегда горизонтально,
 * чтобы читалась при любом повороте предмета. Стоит снаружи предмета, за ручкой с
 * положительной стороны оси — плашка не наезжает ни на ручку, ни на предмет.
 */
function SizeLabel({
	item,
	size,
	hint,
	mmPerPx,
}: {
	item: FloorItem;
	size: { width: number; depth: number };
	hint: SizeHint;
	mmPerPx: number;
}) {
	const unit = useLengthUnit();
	const text = formatMillimeters(hint.value, unit);
	const fontSize = 11 * mmPerPx;
	const width = (text.length * 6.6 + 12) * mmPerPx;
	const height = 18 * mmPerPx;
	const angle = (item.rotation * Math.PI) / 180;
	// Направление от центра к ручке на плане — ось предмета с учётом поворота
	const dir =
		hint.axis === "width"
			? { x: Math.cos(angle), y: Math.sin(angle) }
			: { x: -Math.sin(angle), y: Math.cos(angle) };
	// Плашка встаёт за ручкой с зазором: сколько она занимает вдоль этого направления
	const extent = Math.abs(dir.x) * (width / 2) + Math.abs(dir.y) * (height / 2);
	const reach = (hint.axis === "width" ? size.width : size.depth) / 2 + 12 * mmPerPx + extent;
	const x = item.x + dir.x * reach;
	const y = item.y + dir.y * reach;
	return (
		<g style={{ pointerEvents: "none" }}>
			<rect
				x={x - width / 2}
				y={y - height / 2}
				width={width}
				height={height}
				rx={4 * mmPerPx}
				fill={ACCENT}
			/>
			<text
				x={x}
				y={y}
				fontSize={fontSize}
				fontWeight={600}
				fill="#ffffff"
				textAnchor="middle"
				dominantBaseline="central"
			>
				{text}
			</text>
		</g>
	);
}
